import { useState, useEffect, useCallback, useRef } from 'react';
import { isPushSupported, isPushSubscribed, subscribeToPush } from '../../services/pushService';

const DISMISSED_KEY = 'lb-push-prompt-dismissed';
const DISMISS_DAYS = 30;
const DISPLAY_DURATION = 15000;
const FADE_DURATION = 400;

// `onNavigate` comes from the shell rather than the router — tabbed, this
// renders outside every tab's router.
export default function PushPrompt({ onNavigate }) {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    setFading(true);
    setTimeout(() => {
      setVisible(false);
      setFading(false);
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    }, FADE_DURATION);
  }, []);

  useEffect(() => {
    if (!isPushSupported()) return;

    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed && Date.now() - Number(dismissed) < DISMISS_DAYS * 86400000) return;

    isPushSubscribed().then((subscribed) => {
      if (subscribed) {
        localStorage.setItem(DISMISSED_KEY, String(Date.now()));
        return;
      }
      if (Notification.permission === 'denied') return;

      const INITIAL_DELAY = 10000;
      setTimeout(() => setVisible(true), INITIAL_DELAY);
      timerRef.current = setTimeout(() => dismiss(), DISPLAY_DURATION + INITIAL_DELAY);
    });

    return () => clearTimeout(timerRef.current);
  }, [dismiss]);

  const handleEnable = useCallback(async () => {
    setEnabling(true);
    clearTimeout(timerRef.current);
    try {
      await subscribeToPush();
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
      setFading(true);
      setTimeout(() => setVisible(false), FADE_DURATION);
    } catch {
      dismiss();
    } finally {
      setEnabling(false);
    }
  }, [dismiss]);

  const handleSettings = useCallback(() => {
    clearTimeout(timerRef.current);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
    if (onNavigate) onNavigate('/settings/organization/notifications');
  }, [onNavigate]);

  if (!visible) return null;

  return (
    <div
      style={{
        ...styles.banner,
        opacity: fading ? 0 : 1,
        transform: fading ? 'translateY(12px)' : 'translateY(0)',
        transition: `opacity ${FADE_DURATION}ms ease, transform ${FADE_DURATION}ms ease`,
      }}
    >
      <div style={styles.content}>
        <div style={styles.iconWrap}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#45B7AA"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={styles.title}>Stay in the loop</div>
          <div style={styles.subtitle}>
            Get alerts for reports and system events — even when LexiBridge is closed
          </div>
        </div>
      </div>
      <div style={styles.actions}>
        <button onClick={handleSettings} style={styles.settingsLink}>
          Settings
        </button>
        <button onClick={dismiss} style={styles.dismiss}>
          Not now
        </button>
        <button onClick={handleEnable} disabled={enabling} style={styles.enable}>
          {enabling ? 'Enabling...' : 'Enable'}
        </button>
      </div>
      <div style={styles.progressTrack}>
        <div
          style={{
            ...styles.progressBar,
            animation: `pushPromptCountdown ${DISPLAY_DURATION}ms linear forwards`,
          }}
        />
      </div>
      <style>{`
        @keyframes pushPromptCountdown {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  banner: {
    position: 'fixed',
    bottom: 16,
    left: 16,
    right: 16,
    maxWidth: 480,
    margin: '0 auto',
    background: '#0f1219',
    border: '1px solid rgb(var(--accent-rgb) / 0.2)',
    borderRadius: 12,
    padding: '14px 16px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    zIndex: 50000,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    fontFamily: 'Inter, sans-serif',
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: 'rgb(var(--accent-rgb) / 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: {
    color: '#f1f5f9',
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 2,
  },
  subtitle: {
    color: 'rgba(241,245,249,0.5)',
    fontSize: 12,
    lineHeight: 1.4,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  settingsLink: {
    background: 'none',
    border: 'none',
    color: 'rgb(var(--accent-rgb) / 0.6)',
    fontSize: 12,
    cursor: 'pointer',
    padding: '6px 8px',
    marginRight: 'auto',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
  },
  dismiss: {
    background: 'none',
    border: 'none',
    color: 'rgba(241,245,249,0.4)',
    fontSize: 12,
    cursor: 'pointer',
    padding: '6px 8px',
  },
  enable: {
    background: 'var(--accent)',
    border: 'none',
    borderRadius: 8,
    color: '#06080f',
    fontSize: 13,
    fontWeight: 600,
    padding: '8px 16px',
    cursor: 'pointer',
  },
  progressTrack: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    background: 'rgb(var(--accent-rgb) / 0.1)',
    borderRadius: '0 0 12px 12px',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    background: 'rgb(var(--accent-rgb) / 0.4)',
    borderRadius: '0 0 0 12px',
  },
};
